import moment from "moment";
import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import { isOwnProviderOrderRecipient } from "@/utils/providerOrderDelivery";
import deliverysites from "./submodules/deliverysites";

const state = {
    delivery_type: "deliverysite",
    customer_id: null,

    providerorder: {},
    providerorder_saving: false,
    providerorder_saving_errors: {},

    providerorder_loading: false,
    providerorder_loading_errors: {},

    delivery_date: null,

    provider_id: null,
    provider: null,
    provider_loading: false,
    provider_loading_errors: {},

    deliverysite_id: null,
    deliverysite: null,
    deliverysite_loading: false,
    deliverysite_loading_errors: {},

    updating_providerorder_status: false,
    transmit_to_seller: false,
};

const mutations = {
    updateProviderOrder(state, providerorder) {
        state.providerorder = JSON.parse(JSON.stringify({ order_date: providerorder?.id ? null : moment().format("YYYY-MM-DD"), ...(providerorder || {}) }));
        state.provider_id = providerorder.seller;
        state.customer_id = providerorder.recipient;
        // Handle deliverysite: can be an ID or have deliverysite_data
        state.deliverysite_id = providerorder.deliverysite;
        state.deliverysite = providerorder.deliverysite_data || null;
        // Ensure deliverysite is set in providerorder object
        if (providerorder.deliverysite && !state.providerorder.deliverysite) {
            state.providerorder.deliverysite = providerorder.deliverysite;
        }
        state.delivery_date = providerorder.delivery_date;
        state.delivery_type = providerorder.recipient ? "customer" : "deliverysite";
        state.transmit_to_seller = Boolean(providerorder.seller_entity);
        state.providerorder.transmit_to_seller = state.transmit_to_seller;
        state.provider = providerorder.seller_data || null;
    },
    updateProviderOrderSaving(state, saving) {
        state.providerorder_saving = saving;
    },
    updateProviderOrderSavingErrors(state, errors) {
        state.providerorder_saving_errors = errors;
    },
    updateProviderOrderLoading(state, loading) {
        state.providerorder_loading = loading;
    },
    updateProviderOrderLoadingErrors(state, errors) {
        state.providerorder_loading_errors = errors;
    },
    updateProvider(state, provider) {
        state.provider = provider ? JSON.parse(JSON.stringify(provider)) : null;
        state.provider_id = provider ? provider.id : null;
        state.providerorder.seller = provider ? provider.id : null;
        const sellerIsConnected = Boolean(provider && provider.entity);
        if (sellerIsConnected) {
            if (!state.providerorder.id && state.transmit_to_seller === false) {
                state.transmit_to_seller = true;
            }
            state.providerorder.transmit_to_seller = state.transmit_to_seller;
        } else {
            state.transmit_to_seller = false;
            delete state.providerorder.transmit_to_seller;
        }
    },
    updateProviderId(state, provider_id) {
        state.provider_id = provider_id;
        state.providerorder.seller = provider_id;
    },

    updateDeliverySiteId(state, deliverysite_id) {
        state.deliverysite_id = deliverysite_id;
        state.providerorder.deliverysite = deliverysite_id;
    },
    updateDeliveryType(state, delivery_type) {
        state.delivery_type = delivery_type;
    },
    updateDeliverySiteLoadingErrors(state, errors) {
        state.deliverysite_loading_errors = errors;
    },
    updateDeliverySiteLoading(state, loading) {
        state.deliverysite_loading = loading;
    },
    updateDeliverySite(state, deliverysite) {
        state.deliverysite = deliverysite;
        state.deliverysite_id = deliverysite.id;
        state.providerorder.deliverysite = deliverysite.id;
    },

    updateProviderLoadingErrors(state, errors) {
        state.provider_loading_errors = errors;
    },
    updateProviderLoading(state, loading) {
        state.provider_loading = loading;
    },

    setDefaultDeliverySite(state) {
        if (
            !state.providerorder.deliverysite &&
            state.deliverysites.deliverysites.length == 1
        ) {
            state.deliverysite_id = state.deliverysites.deliverysites[0].id;
            state.providerorder.deliverysite = state.deliverysites.deliverysites[0].id;
        }
    },

    updateOrderDate(state, order_date) {
        state.providerorder.order_date = order_date;
    },
    updateDeliveryDate(state, delivery_date) {
        state.providerorder.delivery_date = delivery_date;
        state.delivery_date = delivery_date;
    },
    updateCustomerId(state, customer_id) {
        state.customer_id = customer_id;
        state.providerorder.recipient = customer_id;
    },
    updateRecipient(state, recipient) {
        state.providerorder.recipient = recipient;
    },
    updateBillingaccount(state, billingaccount) {
        state.providerorder.billingaccount = billingaccount;
    },
    updateTransmitToSeller(state, transmit) {
        state.transmit_to_seller = Boolean(transmit);
        if (state.transmit_to_seller) {
            state.providerorder.transmit_to_seller = true;
        } else {
            state.providerorder.transmit_to_seller = false;
        }
    },
};

const actions = {
    async saveProviderOrder({ commit, dispatch, state }, params) {

        commit("updateProviderOrderSaving", true);
        commit("updateProviderOrderSavingErrors", {});

        let url = "/api/providerorders/";
        let method = axios.post;

        if (state.providerorder.id) {
            url = `/api/providerorders/${state.providerorder.id}/`;
            method = axios.put;
        }
        const providerHasEntity =
            Boolean(state.provider && state.provider.entity) ||
            Boolean(state.providerorder && state.providerorder.seller_entity);

        const payload = JSON.parse(JSON.stringify(state.providerorder || {}));
        if (providerHasEntity) {
            payload.transmit_to_seller = Boolean(state.transmit_to_seller);
        } else {
            delete payload.transmit_to_seller;
        }
        if (state.delivery_type !== "customer") {
            payload.recipient = null;
        }

        try {
            const response = await method(url, payload);
            commit("updateProviderOrderSaving", false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProviderOrderSaving", false);
            commit("updateProviderOrderSavingErrors", error.details);
            throw error;
        } finally {
            dispatch("session/fetchStats", null, { root: true });
        }
    },

    setDeliverySiteId({ commit, dispatch, state }, deliverysite_id) {
        commit("updateDeliverySiteId", deliverysite_id);
    },

    async setDeliveryType({ commit, dispatch, state }, delivery_type) {
        commit("updateDeliveryType", delivery_type);
        commit("updateCustomerId", null);
        commit("updateDeliverySiteId", null);
        if (delivery_type == "deliverysite") {
            await dispatch("deliverysites/init", { filters: { limit: 1000 } });
            await dispatch("deliverysites/fetchDeliverySites");
            commit("setDefaultDeliverySite");
        } else {

            dispatch("customers/fetchCustomers");
            await dispatch("deliverysites/init")
        }
    },
    async setCustomerId({ commit, dispatch, state }, customer_id) {
        commit("updateCustomerId", customer_id);
        if (!customer_id) {
            commit("updateDeliverySiteId", null);
        }
        dispatch("initSources");
        const deliverysites = await dispatch("deliverysites/fetchDeliverySites");
        if (state.providerorder && deliverysites.results.length && !state.providerorder.deliverysite) {
            commit('updateDeliverySiteId', deliverysites.results[0].id);
        }
    },

    async initSources({ commit, dispatch, state }, options = {}) {
        const filters = {
            limit: 1000,
            ordering: "name",
            ...(options.deliverysiteFilters || {}),
        };
        if (state.customer_id) {
            await dispatch("deliverysites/init", {
                url: `/api/customers/${state.customer_id}/deliverysites/`,
                filters,
            });
        } else {
            await dispatch("deliverysites/init", {
                url: `/api/deliverysites/`,
                filters,
            });
        }
    },


    async init({ commit, dispatch, state, rootGetters }, params) {
        const providerorder = params?.providerorder ?? params ?? {};
        const presetDeliverySiteId = providerorder.deliverysite || null;
        const presetDeliverySiteData = providerorder.deliverysite_data || null;
        const ownRecipient = isOwnProviderOrderRecipient(
            providerorder,
            rootGetters["session/current_entity_id"],
        );

        const initialOrder = { ...providerorder };
        delete initialOrder.deliverysite;
        delete initialOrder.deliverysite_data;
        if (!ownRecipient) {
            initialOrder.recipient = null;
            delete initialOrder.recipient_data;
        }
        commit("updateProviderOrder", initialOrder);

        await dispatch("initSources", {
            deliverysiteFilters: params?.deliverysiteFilters,
        });
        try {
            await dispatch("deliverysites/fetchDeliverySites");
        } catch (error) {
            if (!utils.isResourceNotFoundError(error)) {
                throw error;
            }
        }

        if (!presetDeliverySiteId) {
            return;
        }

        let sites = state.deliverysites?.deliverysites || [];
        if (
            presetDeliverySiteData &&
            !sites.some((site) => site.id == presetDeliverySiteId)
        ) {
            sites = [...sites, presetDeliverySiteData];
            commit("deliverysites/updateDeliverySites", sites);
        }

        if (presetDeliverySiteData) {
            commit("updateDeliverySite", presetDeliverySiteData);
        } else {
            commit("updateDeliverySiteId", presetDeliverySiteId);
        }
    },

};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        deliverysites: deliverysites(),
    },
};
